import { useState, useEffect } from "react";
import { api } from "@/lib/api";
import { toast } from "sonner";
import { Radio, Copy, Eye, EyeOff, RefreshCw, ShieldAlert, Check, Clock, CheckCircle2, XCircle, Loader2 } from "lucide-react";

// Founder's Desk panel — shows the editor's reusable Larix RTMPS credentials
// (set up Larix once, tap record any time). Includes a rotate button for
// hygiene and a kill switch for emergencies.
export const PersistentLarixKey = () => {
  const [loading, setLoading] = useState(true);
  const [creds, setCreds] = useState(null);
  const [reveal, setReveal] = useState(false);
  const [rotating, setRotating] = useState(false);
  const [killing, setKilling] = useState(false);
  const [copiedField, setCopiedField] = useState(null);

  const fetchKey = async () => {
    try {
      const { data } = await api.get("/live/persistent-key");
      setCreds(data);
    } catch (err) {
      toast.error(err.response?.data?.detail || "Could not load stream key.");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { fetchKey(); }, []);

  const copy = async (label, value) => {
    try {
      await navigator.clipboard.writeText(value);
      setCopiedField(label);
      toast.success(`${label} copied.`);
      setTimeout(() => setCopiedField(null), 1600);
    } catch {
      toast.error("Copy failed — please select the text manually.");
    }
  };

  const rotate = async () => {
    if (!window.confirm(
      "Rotate the Larix stream key?\n\n" +
      "This creates a fresh stream key and invalidates the current one. You'll need to update Larix on your phone with the new key. Do this if you suspect the key leaked, or as periodic hygiene (every 3–6 months)."
    )) return;
    setRotating(true);
    try {
      const { data } = await api.post("/live/persistent-key/rotate");
      toast.success("Stream key rotated. Update Larix with the new key.");
      setCreds((prev) => prev ? { ...prev, stream_key: data.stream_key, combined_rtmps_url: data.combined_rtmps_url, playback_id: data.playback_id } : prev);
      setReveal(true);
    } catch (err) {
      toast.error(err.response?.data?.detail || "Rotate failed.");
    } finally {
      setRotating(false);
    }
  };

  const kill = async () => {
    if (!window.confirm(
      "End any of your currently-active broadcasts right now?\n\n" +
      "This immediately marks all your active broadcasts as ended. Use this if a broadcast was left running or if you spot something unexpected being pushed to Le Salon."
    )) return;
    setKilling(true);
    try {
      const { data } = await api.post("/live/kill-active");
      if (data.ended_count > 0) {
        toast.success(`Ended ${data.ended_count} active broadcast${data.ended_count === 1 ? "" : "s"}.`);
      } else {
        toast.info("No active broadcasts to end.");
      }
    } catch (err) {
      toast.error(err.response?.data?.detail || "Kill failed.");
    } finally {
      setKilling(false);
    }
  };

  const boxStyle = {
    background: "var(--bg-alt)",
    border: "1px solid var(--border-default)",
    padding: "10px 12px",
    fontFamily: "'JetBrains Mono', 'Courier New', monospace",
    fontSize: 12,
    color: "var(--text-primary)",
    wordBreak: "break-all",
    display: "flex",
    alignItems: "center",
    gap: 8,
    minHeight: 40,
  };

  const btnStyle = {
    background: "transparent",
    border: "1px solid var(--border-dark)",
    padding: "6px 10px",
    fontSize: 10,
    letterSpacing: "0.18em",
    textTransform: "uppercase",
    fontFamily: "'Outfit', sans-serif",
    fontWeight: 600,
    color: "var(--text-primary)",
    cursor: "pointer",
    display: "inline-flex",
    alignItems: "center",
    gap: 6,
    transition: "background 0.15s ease, color 0.15s ease",
  };

  const maskedKey = creds?.stream_key ? creds.stream_key.replace(/./g, "•") : "";

  return (
    <section
      data-testid="persistent-larix-panel"
      style={{
        border: "1px solid var(--border-default)",
        background: "var(--surface)",
        padding: 28,
        marginBottom: 32,
      }}
    >
      <header style={{ display: "flex", alignItems: "center", gap: 12, marginBottom: 6 }}>
        <Radio size={18} strokeWidth={1.5} color="var(--brand-accent)" />
        <h2 className="editorial-h2" style={{ fontSize: 22, margin: 0 }}>
          Persistent Larix stream key
        </h2>
      </header>
      <p className="editorial-body" style={{ fontSize: 14, color: "var(--text-secondary)", margin: "0 0 20px", lineHeight: 1.55 }}>
        Paste the combined RTMPS URL below into Larix on your phone <em>once</em>. Every time you tap record in Larix from now on, you&apos;ll go live on Le Salon — no need to generate a new key each broadcast. Rotate every 3–6 months as good hygiene.
      </p>

      {loading ? (
        <p style={{ color: "var(--text-muted)", fontSize: 13 }}>Loading your stream credentials…</p>
      ) : !creds ? (
        <p style={{ color: "var(--text-muted)", fontSize: 13 }}>Could not load credentials.</p>
      ) : (
        <div style={{ display: "flex", flexDirection: "column", gap: 14 }}>
          {/* PRIMARY — the combined single-URL form works in EVERY version of
              Larix (free + Pro) as well as OBS, Streamlabs, etc. Recommended
              first so users are not confused by the two-field pattern below. */}
          <div>
            <label className="overline" style={{ color: "var(--brand-accent)" }}>
              ★ Combined RTMPS URL — easiest, works in Larix free & Pro
            </label>
            <p style={{ fontSize: 12, color: "var(--text-secondary)", margin: "4px 0 6px", lineHeight: 1.5 }}>
              Paste this ONE line into Larix. If Larix asks for both a &ldquo;URL&rdquo; and a &ldquo;Stream Key&rdquo; — paste this in URL and leave Stream Key empty.
            </p>
            <div style={{ ...boxStyle, marginTop: 6, borderColor: "var(--brand-accent)" }}>
              <span data-testid="larix-combined-url" style={{ flex: 1 }}>
                {reveal ? creds.combined_rtmps_url : creds.combined_rtmps_url.replace(/\/app\/.*/, "/app/••••••••••••••••••••••••")}
              </span>
              <button type="button" data-testid="toggle-reveal-key" onClick={() => setReveal((v) => !v)} style={btnStyle} aria-label={reveal ? "Hide key" : "Reveal key"}>
                {reveal ? <EyeOff size={11} /> : <Eye size={11} />} {reveal ? "Hide" : "Show"}
              </button>
              <button type="button" data-testid="copy-combined-url" onClick={() => copy("Combined URL", creds.combined_rtmps_url)} style={{ ...btnStyle, borderColor: "var(--brand-accent)", color: "var(--brand-accent)" }}>
                {copiedField === "Combined URL" ? <Check size={11} /> : <Copy size={11} />} Copy
              </button>
            </div>
          </div>

          <details style={{ background: "var(--bg-alt)", border: "1px solid var(--border-default)", padding: "10px 14px" }}>
            <summary style={{ cursor: "pointer", fontSize: 12, color: "var(--text-secondary)", letterSpacing: "0.06em" }}>
              Or use them separately (Larix Pro / OBS advanced)
            </summary>
            <div style={{ display: "flex", flexDirection: "column", gap: 10, marginTop: 12 }}>
              <div>
                <label className="overline" style={{ color: "var(--text-muted)" }}>RTMPS URL</label>
                <div style={{ ...boxStyle, marginTop: 6 }}>
                  <span data-testid="larix-rtmps-url" style={{ flex: 1 }}>{creds.rtmps_url}</span>
                  <button type="button" data-testid="copy-rtmps-url" onClick={() => copy("RTMPS URL", creds.rtmps_url)} style={btnStyle}>
                    {copiedField === "RTMPS URL" ? <Check size={11} /> : <Copy size={11} />} Copy
                  </button>
                </div>
              </div>
              <div>
                <label className="overline" style={{ color: "var(--text-muted)" }}>Stream Key</label>
                <div style={{ ...boxStyle, marginTop: 6 }}>
                  <span data-testid="larix-stream-key" style={{ flex: 1 }}>
                    {reveal ? creds.stream_key : maskedKey}
                  </span>
                  <button type="button" data-testid="copy-stream-key" onClick={() => copy("Stream key", creds.stream_key)} style={btnStyle}>
                    {copiedField === "Stream key" ? <Check size={11} /> : <Copy size={11} />} Copy
                  </button>
                </div>
              </div>
            </div>
          </details>

          <div style={{ padding: 12, background: "rgba(120, 34, 45, 0.05)", border: "1px dashed var(--brand-accent)", fontSize: 12, color: "var(--text-secondary)", lineHeight: 1.55 }}>
            <strong style={{ color: "var(--brand-accent)" }}>Safety cap:</strong> every broadcast is limited to a maximum of {creds.max_broadcast_hours} hours of continuous streaming. This bounds worst-case exposure if the key ever leaks.
          </div>

          {/* Security-leak indicator: shows the moment the key was last used
              to push a live stream, so an unexpected timestamp is a red flag. */}
          <div
            data-testid="larix-last-used"
            style={{
              display: "flex",
              alignItems: "center",
              gap: 8,
              fontSize: 12,
              color: "var(--text-muted)",
              fontFamily: "'Outfit', sans-serif",
              letterSpacing: "0.04em",
              padding: "8px 0",
            }}
          >
            <Clock size={13} strokeWidth={1.5} />
            <span>
              Last used:{" "}
              {creds.last_used_at ? (
                <strong style={{ color: "var(--text-primary)", fontWeight: 500 }}>
                  {new Date(creds.last_used_at).toLocaleString(undefined, {
                    weekday: "short",
                    month: "short",
                    day: "numeric",
                    hour: "numeric",
                    minute: "2-digit",
                  })}
                </strong>
              ) : (
                <em style={{ color: "var(--text-muted)" }}>never</em>
              )}
              {creds.last_used_at && (
                <span style={{ marginLeft: 8, color: "var(--text-muted)" }}>
                  {" · if you didn't stream then, rotate the key immediately."}
                </span>
              )}
            </span>
          </div>

          {/* Last recording result — instant ✓ / ⏳ / ✗ feedback so the editor
              never has to wait 5 minutes for "Finalizing" to time out. */}
          {creds.last_broadcast && (() => {
            const lb = creds.last_broadcast;
            let icon, label, tone, hint;
            if (lb.deleted && !lb.has_asset) {
              icon = <XCircle size={14} strokeWidth={2} color="#B33636" />;
              label = "Failed to record";
              tone = "#B33636";
              hint = lb.error_message || "Mux did not receive or record this stream. Check Larix URL/key and try again.";
            } else if (lb.has_asset) {
              icon = <CheckCircle2 size={14} strokeWidth={2} color="#2F7A3F" />;
              label = "Recording ready";
              tone = "#2F7A3F";
              hint = "Available in your archive.";
            } else if (lb.state === "ended") {
              icon = <Loader2 size={14} strokeWidth={2} color="#B37D2C" className="spin" />;
              label = "Processing…";
              tone = "#B37D2C";
              hint = "Mux is finalising the recording — usually 30–90s after you stop.";
            } else if (lb.state === "active") {
              icon = <Radio size={14} strokeWidth={2} color="#D33F3F" />;
              label = "Live now";
              tone = "#D33F3F";
              hint = "Your Larix stream is currently active.";
            } else {
              icon = <Loader2 size={14} strokeWidth={2} color="#666" />;
              label = lb.state || "Unknown";
              tone = "#666";
              hint = "";
            }
            return (
              <div
                data-testid="larix-last-recording"
                style={{
                  display: "flex",
                  alignItems: "flex-start",
                  gap: 10,
                  padding: "10px 14px",
                  background: "var(--bg-alt)",
                  border: `1px solid ${tone}33`,
                  borderLeft: `3px solid ${tone}`,
                  fontSize: 12,
                  color: "var(--text-secondary)",
                  lineHeight: 1.55,
                }}
              >
                <span style={{ paddingTop: 2, flexShrink: 0 }}>{icon}</span>
                <div style={{ minWidth: 0, flex: 1 }}>
                  <div style={{
                    fontFamily: "'Outfit', sans-serif",
                    fontSize: 10,
                    letterSpacing: "0.22em",
                    textTransform: "uppercase",
                    fontWeight: 700,
                    color: tone,
                    marginBottom: 2,
                  }}>
                    Last broadcast · {label}
                  </div>
                  <div className="font-serif" style={{ fontSize: 13, fontStyle: "italic", color: "var(--text-primary)", marginBottom: 2 }}>
                    {lb.title || "Untitled"}
                  </div>
                  {hint && <div style={{ fontSize: 11, color: "var(--text-muted)" }}>{hint}</div>}
                </div>
              </div>
            );
          })()}

          <div style={{ display: "flex", flexWrap: "wrap", gap: 10, marginTop: 6 }}>
            <button
              type="button"
              data-testid="rotate-larix-key"
              onClick={rotate}
              disabled={rotating}
              style={{ ...btnStyle, borderColor: "var(--brand-accent)", color: "var(--brand-accent)", opacity: rotating ? 0.5 : 1 }}
            >
              <RefreshCw size={11} strokeWidth={2} className={rotating ? "spin" : ""} /> {rotating ? "Rotating…" : "Rotate stream key"}
            </button>
            <button
              type="button"
              data-testid="kill-active-broadcast"
              onClick={kill}
              disabled={killing}
              style={{ ...btnStyle, borderColor: "#B33636", color: "#B33636", opacity: killing ? 0.5 : 1 }}
            >
              <ShieldAlert size={11} strokeWidth={2} /> {killing ? "Ending…" : "End any active broadcast"}
            </button>
          </div>
        </div>
      )}
      <style>{`
        .spin { animation: salonSpin 0.9s linear infinite; }
        @keyframes salonSpin { to { transform: rotate(360deg); } }
      `}</style>
    </section>
  );
};

export default PersistentLarixKey;
